import styled from 'styled-components';
import { Button } from 'ui/button';
import { Tooltip } from 'ui/tooltip';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button/constants';
import { ModalFooter, ModalBody, ModalHeader, Modal, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { useModalContext } from 'utils/contexts/modal';
import { Body2NoMargin } from 'modules/frequencyManagement/components/Content.styles';
import { spacing } from 'ui/theme';

export const UnsavedChangesTestIds = {
  FreqManageUnsavedChangesModalTestId: 'freq-manage-unsaved-changes-modal',
  FreqManageUnsavedChangesModalLeaveButtonTestId: 'freq-manage-unsaved-changes-modal-leave-button',
  FreqManageUnsavedChangesModalSaveButtonTestId: 'freq-manage-unsaved-changes-modal-save-button',
  FreqManageUnsavedChangesModalUnsavedChangesNames:
    'freq-manage-unsaved-changes-modal-unsaved-changes-names',
};
export const UnsavedChangesModalId = 'frequency-management-unsaved-changes-modal-id';
export const ConfirmChangesModalId = 'frequency-management-confirm-changes-modal-id';

export const ModalHeaderUnsavedText = 'Unsaved Changes';
export const ModalHeaderConfirmText = 'Confirm changes';
export const ModalBodyText =
  'The following mediums and message types have been updated. Do you want to save your changes?';
export const ModalLeaveWithoutSavingText = 'Leave without saving';
export const ModalSaveAndExitText = 'Save and exit';
const DisabledTooltipText =
  "Oops! There's at least one error to fix before you can save these changes.";

const SpacedDiscList = styled.ul`
  list-style-type: disc;
  width: 100%;
  max-height: ${spacing.x3};
  margin-top: ${spacing.half};
`;

export type UnsavedChangesModalProps = {
  isLoading?: boolean;
  isValid: boolean;
  onClose: () => void;
  onLeave: () => void;
  onSave: () => void;
  unsavedChangesNames?: string[];
};

export const UnsavedChangesModal = ({
  isLoading = false,
  isValid,
  onClose,
  onLeave,
  onSave,
  unsavedChangesNames = [],
}: UnsavedChangesModalProps) => {
  const { modalState } = useModalContext<string>();
  const isUnsavedChangesModal = modalState === UnsavedChangesModalId;
  if (modalState !== UnsavedChangesModalId && modalState !== ConfirmChangesModalId) return null;

  const headerText = isUnsavedChangesModal ? ModalHeaderUnsavedText : ModalHeaderConfirmText;
  const unsavedChangesList = unsavedChangesNames.map((text) => <li key={text}>{text}</li>);
  return (
    <Modal
      withPortal
      onClose={onClose}
      isModalOpen={true}
      id={UnsavedChangesTestIds.FreqManageUnsavedChangesModalTestId}
      data-test={UnsavedChangesTestIds.FreqManageUnsavedChangesModalTestId}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.WARNING}
    >
      <ModalHeader autoFocus={false}>{headerText}</ModalHeader>
      <ModalBody>
        <Body2NoMargin>{ModalBodyText}</Body2NoMargin>
        {unsavedChangesList.length > 0 && (
          <SpacedDiscList
            data-test={UnsavedChangesTestIds.FreqManageUnsavedChangesModalUnsavedChangesNames}
          >
            {unsavedChangesList}
          </SpacedDiscList>
        )}
      </ModalBody>
      <ModalFooter>
        {isUnsavedChangesModal && (
          <Button
            data-test={UnsavedChangesTestIds.FreqManageUnsavedChangesModalLeaveButtonTestId}
            onClick={onLeave}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.SECONDARY}
          >
            {ModalLeaveWithoutSavingText}
          </Button>
        )}
        <Tooltip isUsedOnButton isEnabled={!isValid} tooltipContent={DisabledTooltipText}>
          <Button
            isDisabled={isLoading || !isValid}
            isLoading={isLoading}
            data-test={UnsavedChangesTestIds.FreqManageUnsavedChangesModalSaveButtonTestId}
            onClick={onSave}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.PRIMARY}
          >
            {ModalSaveAndExitText}
          </Button>
        </Tooltip>
      </ModalFooter>
    </Modal>
  );
};
